import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { uniq } from 'lodash';

import { withStyles } from '@material-ui/core/styles';
import ListItem from '@material-ui/core/ListItem';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import RootRef from '@material-ui/core/RootRef';

import Tooltip from 'components/common/Tooltip';
import InventoryHCPCCodes from 'components/inventory/InventoryHCPCCodes';
import Button from 'components/common/Button';
import Avatar from 'components/common/Avatar';
import IconBox from 'components/icons/IconBox';
import IconTransmit from 'components/icons/IconTransmit';
import IconCash from 'components/icons/IconCash';

import formatSinglePlural from 'utils/formatSinglePlural';
import formatPrice from 'utils/formatPrice';

import pxToRem from 'styles/pxToRem';

const styles = theme => ({
  root: {
    padding: '18px 25px 10px 24px',
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
      '& $buttonAdd': {
        display: 'inline-block',
      },
    },
    '& + $root': {
      borderTop: `1px solid ${theme.palette.primary.A100}`,
    },
    [theme.breakpoints.down('sm')]: {
      padding: 8,
    },
  },
  rootSelected: {
    backgroundColor: theme.palette.grey[100],
  },
  typography: {
    fontSize: pxToRem(13),
  },
  gridItemButton: {
    alignSelf: 'center',
    textAlign: 'right',
  },
  codes: {
    marginTop: 10,
  },
  typographyItems: {
    marginTop: 16,
  },
  buttonAdd: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'inline-block',
    },
  },
  avatar: {
    width: 80,
    height: 80,
  },
  iconBox: {
    width: 34,
    height: 34,
  },
  iconTransmit: {
    width: 14,
    height: 14,
  },
  iconCash: {
    width: 20,
    height: 20,
  },
  tooltipRetailUsed: {
    marginLeft: 4,
    verticalAlign: 'middle',
    color: theme.palette.primary[500],
  },
});

const InventorySearchResultsItem = ({
  onAddButtonClick, selected, rootRef, classes, ...otherProps
}) => {
  let imageSrc;
    if (otherProps.Image.length > 0) {
      imageSrc = process.env.REACT_APP_API_ROOT
        ? `${process.env.REACT_APP_API_ROOT}${otherProps.Image[0]['File Url']}${otherProps.Image[0]['File Name']}`
        : `${otherProps.Image[0]['File Url']}${otherProps.Image[0]['File Name']}`;
    }
  const listItem = (
    <ListItem
      className={classNames(classes.root, {
        [classes.rootSelected]: selected,
      })}
      onClick={() => { onAddButtonClick(otherProps); }}
    >
      <Grid
        container
        justify="flex-start"
        alignItems="flex-start"
        spacing={8}
        wrap="nowrap"
      >
        <Grid item>
          <Avatar
            className={classes.avatar}
            src={imageSrc}
            variant="square"
            placeholderImage={(
              <IconBox className={classes.iconBox} />
            )}
          />
        </Grid>
        <Grid
          item
          xs={2}
        >
          <Typography className={classes.typography}>
            {otherProps['Product Name']}
          </Typography>
          <InventoryHCPCCodes
            className={classes.codes}
            {...{ 'HCPC Codes': otherProps['HCPC Codes'].map(item => (
              item.map(nestedItem => nestedItem['HCPC S Code'])
            )) }}
          />
        </Grid>
        <Grid
          item
          xs={1}
        >
          <Typography className={classes.typography}>
            {otherProps.Model}
          </Typography>
        </Grid>     
        <Grid
          item
          xs={1}
        >
          {otherProps.Vendor['Vendor Name'] &&
            <Typography className={classes.typography}>
              {otherProps.Vendor['Vendor Name']}
            </Typography>
          }
        </Grid>
        <Grid
          item
          xs={1}
        >
          <Typography className={classes.typography}>
            {otherProps.Category.Name}
          </Typography>
        </Grid>
        <Grid
          item
          xs={1}
        >
          <Typography className={classes.typography}>
            {otherProps.Condition.Name}
          </Typography>
        </Grid>
        <Grid
          item
          xs={1}
        >
          <Typography
            className={classes.typography}
          >
            {otherProps['Stock Line Balance']}
          </Typography>
        </Grid>   
        <Grid
          item
          xs={1}
        >
          {(otherProps['Last Arrival Stock Line'] && otherProps['Last Arrival Stock Line']['Retail Price']) &&
            <Fragment>
              <Typography className={classes.typography}>
                {formatPrice(otherProps['Last Arrival Stock Line']['Retail Price'])}
                {otherProps['Last Arrival Stock Line']['Is Retail Price For Claims'] &&
                  <Tooltip
                    className={classes.tooltipRetailUsed}
                    content="Used for Claims"
                    placement="bottom"
                    color="dark"
                    disableGutters
                  >
                    <IconTransmit className={classes.iconTransmit} />
                  </Tooltip>
                }
                {otherProps['Last Arrival Stock Line']['Is Retail Price For Self Pay'] &&
                  <Tooltip
                    className={classes.tooltipRetailUsed}
                    content="Used for Self Pay"
                    placement="bottom"
                    color="dark"
                    disableGutters
                  >
                    <IconCash className={classes.iconCash} />
                  </Tooltip>
                }
              </Typography>
            </Fragment>
          }
        </Grid>
        <Grid
          item
          xs={1}
        >
          {(otherProps.Facility && otherProps.Facility.length > 0) &&
            <Typography className={classes.typography}>
              {uniq(otherProps.Facility.map(item => item['Company Info']['Company Name'])).join(', ')}
            </Typography>
          }
        </Grid>
        <Grid
          item
          xs={1}
        >
          <Typography className={classes.typography}>
            {otherProps['Last Arrival Stock Line'] && otherProps['Last Arrival Stock Line'].Warehouse ? otherProps['Last Arrival Stock Line'].Warehouse.Name : ''}
          </Typography>
        </Grid>
        <Grid
          item
          xs={1}
          className={classes.gridItemButton}
        >
          <Button
            variant="raised"
            color="primary"
            className={classes.buttonAdd}
            onMouseDown={() => { onAddButtonClick(otherProps); }}
          >
            Add
          </Button>
        </Grid>
      </Grid>
    </ListItem>
  );
  if (rootRef) {
    return (
      <RootRef
        rootRef={rootRef}
      >
        {listItem}
      </RootRef>
    );
  }
  return listItem;
};

InventorySearchResultsItem.propTypes = {
  'Product Name': PropTypes.string.isRequired,
  Vendor: PropTypes.shape({
    'Vendor Name': PropTypes.string,
  }),
  Model: PropTypes.string,
  'Stock Line Balance': PropTypes.number.isRequired,
  'HCPC Codes': PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.shape({
      'HCPC S Code': PropTypes.string,
      ID: PropTypes.number,
    })),
  ).isRequired,
  Facility: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })),
  onAddButtonClick: PropTypes.func,
  selected: PropTypes.bool,
  rootRef: PropTypes.func,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    rootSelected: PropTypes.string.isRequired,
    typography: PropTypes.string.isRequired,
    gridItemButton: PropTypes.string.isRequired,
    codes: PropTypes.string.isRequired,
    typographyItems: PropTypes.string.isRequired,
    buttonAdd: PropTypes.string.isRequired,
  }).isRequired,
};

InventorySearchResultsItem.defaultProps = {
  Vendor: null,
  onAddButtonClick: () => {},
  selected: false,
  rootRef: null,
  Model: null,
  Facility: null,
};

export default withStyles(styles)(InventorySearchResultsItem);



// WEBPACK FOOTER //
// ./src/components/inventory/InventorySearch/InventorySearchResultsItem.js